import type {
  CampaignCreationCapabilities,
  SmartCreationBrowserDraft,
  SmartCreationReviewAdGroup,
  SmartCreationReviewBidPreviewInput,
  SmartCreationReviewNegative,
  SmartCreationReviewTarget,
  SmartCreationReviewWorkspace,
  SmartCreationStrategyTargetProvenance,
  SmartCreationStrategyTargetSource,
} from "@adpilot/shared";
import { type AddedNegative, type AddedTarget, quickTargetIdentity } from "../review-quick-targets";

export interface Entry {
  id: string;
  label: string;
  match: string;
  bid: string;
  source: SmartCreationStrategyTargetSource | "user" | "legacy" | "unknown";
  performanceUnverified?: boolean;
  original?: SmartCreationReviewTarget;
  negative?: SmartCreationReviewNegative;
  addition?: AddedTarget;
  negativeAddition?: AddedNegative;
}

export function provenanceSource(
  provenance: SmartCreationStrategyTargetProvenance,
): Entry["source"] {
  if (provenance.mode !== "entered") return provenance.originalRecommendation.source;
  return provenance.origin === "user"
    ? "user"
    : provenance.origin === "system_required"
      ? "system_required"
      : "legacy";
}

export function targetSource(
  target: SmartCreationReviewTarget,
  plan?: SmartCreationBrowserDraft,
): Entry["source"] {
  const persisted = plan?.draft.strategy.branches
    .find((branch) => branch.kind === target.strategyKind)
    ?.targets.find((candidate) => candidate.id === target.sourceTargetId);
  if (persisted) return provenanceSource(persisted.provenance);
  const recommended = plan?.strategyRecommendation?.branches
    .find((branch) => branch.kind === target.strategyKind)
    ?.targets.find((candidate) => candidate.id === target.sourceTargetId);
  if (recommended) return recommended.source;
  if (target.sourceTargetId.startsWith("quick:")) return "user";
  return target.type === "automatic" ? "system_required" : "unknown";
}
export function targetEntry(
  target: SmartCreationReviewTarget,
  plan?: SmartCreationBrowserDraft,
): Entry {
  // Performance validation still distinguishes shop evidence from unverified target candidates.
  const provenance = plan?.draft.strategy.branches
    .find((branch) => branch.kind === target.strategyKind)
    ?.targets.find((item) => item.id === target.sourceTargetId)?.provenance;
  const origin =
    provenance?.mode === "entered"
      ? provenance.origin === "system_required"
        ? "system_default"
        : provenance.origin === "user"
          ? "user_entered"
          : "preview_generator"
      : provenance?.originalRecommendation.source;
  return {
    id: target.ref.lineageKey,
    label:
      target.type === "keyword"
        ? target.keyword
        : target.type === "product"
          ? target.asin
          : target.type === "category"
            ? target.categoryId
            : target.type === "category_brand"
              ? `${target.categoryId} / ${target.brandId}`
              : target.expressionType,
    match:
      target.type === "keyword"
        ? target.matchType.effective
        : target.type === "product"
          ? (target.matchType ?? "exact")
          : target.type === "automatic"
            ? target.expressionType
            : target.type,
    bid: String(target.bid.effective),
    source: targetSource(target, plan),
    performanceUnverified:
      target.strategyKind === "performance" &&
      !["current_shop_target_performance", "current_shop_search_term"].includes(origin ?? ""),
    original: target,
  };
}
export function negativeEntry(target: SmartCreationReviewNegative): Entry {
  return {
    id: target.ref.lineageKey,
    label: target.type === "keyword" ? target.text.effective : target.asin.effective,
    match: target.type === "keyword" ? target.matchType.effective : "product",
    bid: "",
    source: "unknown",
    negative: target,
  };
}
export function entryIdentity(entry: Pick<Entry, "match" | "label">): string {
  return quickTargetIdentity(entry.match, entry.label);
}

export function matchTranslationKey(match: string): string {
  return match === "NEGATIVE_EXACT"
    ? "negativeExact"
    : match === "NEGATIVE_PHRASE"
      ? "negativePhrase"
      : match;
}

export function bidExpression(
  entry: Entry,
): SmartCreationReviewBidPreviewInput["expressions"][number] | null {
  if (entry.original?.type === "automatic")
    return { type: "automatic", expressionType: entry.original.expressionType };
  if (entry.original?.type === "category")
    return { type: "category", categoryId: entry.original.categoryId };
  if (
    (entry.original?.type === "product" || entry.addition?.type === "product") &&
    entry.match === "exact"
  )
    return { type: "product", asin: entry.label, matchType: "exact" };
  if ((entry.original && entry.original.type !== "keyword") || entry.addition?.type === "product")
    return null;
  if (entry.match === "broad" || entry.match === "phrase" || entry.match === "exact")
    return { type: "keyword", keyword: entry.label, matchType: entry.match };
  return null;
}

export function bidExpressionKey(
  expression: SmartCreationReviewBidPreviewInput["expressions"][number],
): string {
  if (expression.type === "keyword")
    return `keyword:${expression.matchType}:${expression.keyword.trim().toLocaleLowerCase("en-US")}`;
  if (expression.type === "product") return `product:${expression.asin.toUpperCase()}`;
  if (expression.type === "category") return `category:${expression.categoryId}`;
  return `automatic:${expression.expressionType}`;
}

export function groupLane(
  workspace: SmartCreationReviewWorkspace,
  group: SmartCreationReviewAdGroup,
) {
  const target = workspace.graph.targets.find(
    (item) => item.adGroupLineageKey === group.ref.lineageKey,
  );
  const match =
    target?.type === "keyword"
      ? target.matchType.effective
      : target?.type === "product"
        ? (target.matchType ?? "exact")
        : (target?.type ?? "automatic");
  return {
    key: `${target?.strategyKind ?? "automatic"}:${target?.type ?? "automatic"}:${match}`,
    strategy: target?.strategyKind ?? "automatic",
    type: target?.type ?? "automatic",
    match,
  };
}

export function validBid(value: string, limits?: CampaignCreationCapabilities["bid"]): boolean {
  const scale = 10 ** (limits?.decimals ?? 2);
  const amount = Number(value);
  return (
    value.trim() !== "" &&
    Number.isFinite(amount) &&
    amount >= (limits?.min ?? 0.01) &&
    amount <= (limits?.max ?? 1_000_000) &&
    Math.abs(amount * scale - Math.round(amount * scale)) <= 1e-7
  );
}

export type GroupLane = ReturnType<typeof groupLane>;

/** Strategy targets of the ad group's lane that the preview has not placed yet. */
export function strategyCandidatesByGroup(
  baseline: SmartCreationReviewWorkspace,
  plan: SmartCreationBrowserDraft | undefined,
  precise: boolean,
): Record<string, Entry[]> {
  return Object.fromEntries(
    baseline.graph.adGroups.map((candidateGroup) => {
      const lane = groupLane(baseline, candidateGroup);
      const branch = plan?.draft.strategy.branches.find((item) => item.kind === lane.strategy);
      const existing = new Set(
        baseline.graph.targets
          .filter((item) => item.adGroupLineageKey === candidateGroup.ref.lineageKey)
          .map((item) => entryIdentity(targetEntry(item, plan))),
      );
      const candidates: Entry[] = [];
      const recommended =
        plan?.strategyRecommendation?.branches.find((item) => item.kind === lane.strategy)
          ?.targets ?? [];
      if (!precise && branch && branch.kind !== "automatic")
        for (const candidate of [...branch.targets, ...recommended]) {
          const intent = candidate.intent;
          if (
            candidate.productAsins &&
            !candidateGroup.products.some((product) =>
              candidate.productAsins?.includes(product.asin),
            )
          )
            continue;
          if (intent.type !== lane.type || (intent.type !== "keyword" && intent.type !== "product"))
            continue;
          if (intent.type === "keyword" && intent.matchType !== lane.match) continue;
          const label = intent.type === "keyword" ? intent.keyword : intent.asin;
          const match = intent.matchType ?? "exact";
          const identity = quickTargetIdentity(match, label);
          if (existing.has(identity)) continue;
          existing.add(identity);
          const id = `candidate:${crypto.randomUUID()}`;
          const addition: AddedTarget = {
            ...intent,
            ref: { kind: "target", itemKey: null, lineageKey: id },
            sourceTargetId: candidate.id,
            strategyKind: branch.kind,
            bid: candidate.bid,
          };
          candidates.push({
            id,
            label,
            match,
            bid: String(candidate.bid),
            source:
              "source" in candidate ? candidate.source : provenanceSource(candidate.provenance),
            addition,
          });
        }
      return [candidateGroup.ref.lineageKey, candidates];
    }),
  );
}
